import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TokenStorage, type OAuth2Tokens } from '@forgerock/javascript-sdk';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { BehaviorSubject, interval, timer as observableTimer, Subject, Subscription } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';

import {
  MAX_TOKEN_RETRY_ATTEMPTS,
  TOKEN_REFRESH_INTERVAL,
  TOKEN_REFRESH_THRESHOLD_SECONDS,
  TOKEN_RETRY_DELAY_MS
} from '@amfam/shared/models/lib/login/auth.constant';
import { selectCustomerGroupId } from '@amfam/shared/utility/brand/lib/+state/brand.selectors';
import { HOMESITE_GROUP_IDS } from '@amfam/shared/utility/brand/lib/services/partner-mappings';
import { NavigationService } from '@amfam/shared/utility/navigation/lib/services/navigation.service';
import {
  LOGOUT_ORIGINS,
  LogoutOrigin
} from '@amfam/shared/utility/shared-services/lib/core/logout-origin';
import { UtilService } from '@amfam/shared/utility/shared-services/lib/core/util.service';
import { EventDispatcherService } from '@amfam/shared/utility/shared-services/lib/event-bus/event-dispatcher.service';
import { SessionEventsService } from '@amfam/shared/utility/shared-services/lib/event-bus/session-events.service';
import { LOCAL_STORAGE_KEYS } from '@amfam/shared/utility/shared-services/lib/storage/storage-keys';
import {
  formatErrorMessage,
  getErrorMessage
} from '@amfam/shared/utility/shared-services/lib/utils/error.util';
import { LoadingSpinnerService } from '@amfam/ui-kit/lib/loading-spinner/services/loading-spinner.service';
import { ComponentViewService } from '@amfam/ui-kit/lib/shared-components/component-view.service';

import { ForgerockAuthService } from '../../core/auth/forgerock-auth.service';

@Injectable()
export class SessionActivityService {
  private forgerockAuthService = inject(ForgerockAuthService);
  private navigationService = inject(NavigationService);
  private sessionEventsService = inject(SessionEventsService);
  private eventDispatchedService = inject(EventDispatcherService);
  private store = inject(Store);
  private componentViewService = inject(ComponentViewService);
  private utilService = inject(UtilService);
  private spinner = inject(LoadingSpinnerService);
  private isHomesitePartner = toSignal(
    this.store.select(selectCustomerGroupId).pipe(map(id => HOMESITE_GROUP_IDS.includes(id))),
    { initialValue: false }
  );

  private isUserActive = false; // user has registered activity since the last keep-alive
  private timersStarted = false; // activity polling and warning timers started

  // A handle on the interval that tracks when to send a keep alive
  private activityPollSub: Subscription | undefined; // timer for checking user activity
  private popupSub: Subscription | undefined; // timer for warning popup
  private timeoutSub: Subscription | undefined; // timer for expire session
  private refreshIntervalSub: Subscription | undefined; // timer for token refresh interval
  private forgerockIdSub: Subscription | undefined; // forgerockId$ subscription

  private sessionWarningDelay = 720000; // 12 minutes
  private activityPollingDelay = 60000; // 1 minute
  private activityPollingDelayHomesite = 3600000; // 1 hour
  private expireSessionDelay = 120000; // 2 minutes

  // Subjects used by session-timeout component to manage visibility of warning
  private _showSessionWarning$: Subject<boolean> = new BehaviorSubject<boolean>(false);

  private stop$ = new Subject<void>();

  /**
   * The currentUser$ auth subscription is responsible for starting the
   *
   * session tracking timers and polling timers when the user logs in as
   * well as responsible for tearing them down when the user logs out manually
   * or by the system. As the currentUser is updated by API activity or the
   */
  constructor() {
    const authService = this.forgerockAuthService;

    authService.currentUser$.pipe(takeUntil(this.stop$)).subscribe(currentUser => {
      if (currentUser && currentUser.trackId && currentUser.trackId !== '') {
        this.startTimers();
      }
    });
    this.sessionEventsService.userActivity$.subscribe(() => this.registerUserActivity());
    this.forgerockIdSub = this.forgerockAuthService.forgerockId$
      .pipe(
        takeUntil(this.stop$),
        filter(frId => !!frId),
        tap(() => this.refreshSession())
      )
      .subscribe();

    // Detect if we're returning from ForgeRock login vs iframe refresh
    this.detectReturnFromForgeRock();
  }

  /**
   * Detects if the app is returning from ForgeRock login or iframe refresh
   * Helps differentiate between:
   * 1. Silent iframe refresh (background, no user sees redirect)
   * 2. Full login redirect (user saw ForgeRock login page)
   */
  private detectReturnFromForgeRock() {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const isInIframe = window.parent !== window.self;

    if (code && state) {
      if (isInIframe) {
        this.utilService.log('[Auth] Returned from silent iframe refresh');
        // This is iframe callback - session storage intact
      } else {
        this.utilService.log('[Auth] Returned from full login redirect');
        // This is full login callback - session was cleared before redirect
        // Initialize fresh session state here if needed
      }
    }
  }

  /**
   * User activity handlers are wired in the app root to detect
   * when the user is interacting with the application.
   */
  registerUserActivity() {
    this.isUserActive = true;
  }

  /**
   * Observable for communicating when the session timeout warning should be shown / hidden
   */
  get showSessionWarning$() {
    return this._showSessionWarning$.asObservable();
  }

  /**
   * Happens if user clicks 'I'm done' during session expiration warning. Logs the user out and
   * cancels the sessionExpirationWarning and activity polling timers. Routes to /login page.
   */
  userDone() {
    this._showSessionWarning$.next(false); // closes the warning
    this.logout('/login', LOGOUT_ORIGINS.SESSION_USER_DONE);
  }

  /**
   * Accept user input from the session expiration warning dialogue to continue with the user's
   * session. This is seperate from the private sendKeepAlive() method in that the private method
   * also handles the starting of the primary sessionExpirationWarning & userActivityPolling timers
   */
  keepAlive() {
    this._showSessionWarning$.next(false); // closes the warning
    this.registerUserActivity();
    this.sendKeepAlive();
  }

  /**
   * pop the session warning notification if the user is inactive
   */
  private handleSessionTimeoutWarning() {
    if (this.isUserActive) {
      this.sendKeepAlive();
    } else {
      this.cancelTimers();
      const popupTimer = observableTimer(this.expireSessionDelay);
      this.popupSub = popupTimer.subscribe(_f => this.expireSession());
      this._showSessionWarning$.next(true); // shows the warning
    }
  }

  /**
   * called when the user doesn't click I'm still here.
   * This will logout the user after routing to /session-expired
   */
  private expireSession() {
    this._showSessionWarning$.next(false); // closes the warning
    this.logout('/session-expired', LOGOUT_ORIGINS.SESSION_IDLE_TIMEOUT);
  }

  private differenceInSeconds(dateLeft: Date, dateRight: Date): number {
    // fromJSDate is safe here - both params are typed and used as real Date objects, not
    // strings being re-parsed, so there's no bare-date-as-UTC ambiguity to worry about.
    const diff =
      (DateTime.fromJSDate(dateLeft).toMillis() - DateTime.fromJSDate(dateRight).toMillis()) / 1000;
    return diff > 0 ? Math.floor(diff) : Math.ceil(diff);
  }

  private async refreshSession() {
    this.utilService.log('[Auth] Refresh token process started!');

    this.refreshIntervalSub?.unsubscribe();
    this.stop$.next();

    let timeUntilExpiry = 0;
    let expiry = 0;
    let tokens: OAuth2Tokens | null = (await TokenStorage.get()) as OAuth2Tokens | null;
    if (tokens) {
      if (tokens.tokenExpiry) expiry = tokens.tokenExpiry;
      timeUntilExpiry = this.differenceInSeconds(new Date(expiry), new Date());
    } else {
      this.utilService.log(`[Auth] No tokens found to refresh!`);
    }
    this.utilService.log(
      `[Auth] Fetch new token after ${timeUntilExpiry} seconds at ${this.utilService.timeAfter(timeUntilExpiry)}`
    );

    this.refreshIntervalSub = interval(TOKEN_REFRESH_INTERVAL)
      .pipe(
        takeUntil(this.stop$),
        tap(async () => {
          try {
            if (timeUntilExpiry <= TOKEN_REFRESH_THRESHOLD_SECONDS) {
              this.utilService.log(
                `[Auth] Refreshing token with ${timeUntilExpiry} seconds remaining at ${this.utilService.timeAfter(timeUntilExpiry)}`
              );
              tokens = await this.getTokensWithRetry(
                MAX_TOKEN_RETRY_ATTEMPTS,
                TOKEN_RETRY_DELAY_MS
              );
              this.utilService.log(
                `[Auth] New Tokens acquired -- token expiry : ${new Date(tokens?.tokenExpiry || 0).toLocaleTimeString()}`
              );
              if (tokens) {
                this.eventDispatchedService.tokenRefreshed(tokens.accessToken);
                expiry = tokens.tokenExpiry || 0;
                this.utilService.log(
                  `[Auth] New Expiry in ${this.differenceInSeconds(new Date(expiry), new Date())} seconds at ${this.utilService.timeAfter(this.differenceInSeconds(new Date(expiry), new Date()))}`
                );
              } else {
                this.utilService.error(
                  `[Auth] Refresh Token Failed! Expiry time -- ${new Date(expiry || 0).toTimeString()}`
                );
                if (this.componentViewService.inWebview)
                  this.componentViewService.sendToApp({ type: 'abort' });
                else this.logout('/session-expired', LOGOUT_ORIGINS.TOKEN_REFRESH_FAILED);
              }
            }
            if (expiry > 0)
              timeUntilExpiry = this.differenceInSeconds(new Date(expiry), new Date());
          } catch {}
        })
      )
      .subscribe();
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async getTokensWithRetry(maxRetries = 3, retryDelayMs = 5000) {
    this.utilService.log(`[Auth] Token retrieval started!`);
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        localStorage.setItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN, '1');
        const tokens = await this.forgerockAuthService.getRefreshTokens();
        if (tokens) {
          this.utilService.log(`[Auth] Token retrieval succeeded on attempt ${attempt + 1}`);
          return tokens;
        }
        this.utilService.error(`[Auth] Token retrieval attempt ${attempt + 1} returned null`);
      } catch (error) {
        this.utilService.error(
          `[Auth] Token retrieval attempt ${attempt + 1} failed. -- Error : ${getErrorMessage(error)}`,
          {
            error: formatErrorMessage(error)
          }
        );
      }

      // Retry delay applies to both errors and null responses
      if (attempt < maxRetries) {
        this.utilService.log(`[Auth] Token retrieval - Retrying in ${retryDelayMs}ms...`);
        await this.delay(retryDelayMs);
      } else {
        this.utilService.error(`[Auth] All ${maxRetries + 1} token retrieval attempts failed!`);
      }
    }
    localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
    return null;
  }

  stopRefresh() {
    this.stop$.next();
    this.refreshIntervalSub?.unsubscribe();
    this.forgerockIdSub?.unsubscribe();
  }

  /**
   * Call refresh to update the token. Actual implementation details
   * currently unknown. The session expiration warning needs to be turned back on in
   * addition to the periodic polling of user activity.
   */
  private sendKeepAlive() {
    if (this.isUserActive) {
      this.cancelTimers();
      this.startTimers();
    }
  }

  /**
   * logout and reload the app
   *
   * @param origin which path asked for the logout - this method has four callers with four
   * different meanings, so the origin has to come from the caller to stay useful in DataDog.
   * See docs/auth/forgerock-token-revoke.md
   */
  public async logout(path: string, origin: LogoutOrigin): Promise<void> {
    this.spinner.start({ loadingMessage: 'You are being logged out', blockActions: true });
    this.stopRefresh();
    this.cancelTimers();
    const redirect = this.navigationService.getCommands(path);
    await this.forgerockAuthService.logoutAndRevoke(origin);
    this.forgerockAuthService.logout();
    window.location.href = redirect.join('/');
  }

  /**
   * Start the timers to show the session expiration warning and check user activity.
   *
   * THIS SHOULD ONLY be called from within the currentUser$ Subscription.
   */
  private startTimers() {
    if (!this.timersStarted) {
      this.isUserActive = false;
      this.timersStarted = true;
      // start timer forsession expiration warning.
      const timeoutTimer = observableTimer(this.sessionWarningDelay);
      this.timeoutSub = timeoutTimer.subscribe(() => this.handleSessionTimeoutWarning());
      // start timer for checking user activity
      const pollingDelay = this.isHomesitePartner()
        ? this.activityPollingDelayHomesite
        : this.activityPollingDelay;
      const activityPollTimer = observableTimer(pollingDelay, pollingDelay);
      this.activityPollSub = activityPollTimer.subscribe(() => this.sendKeepAlive());
    }
  }

  /**
   * Cancel all the timers
   */
  private cancelTimers() {
    this.timersStarted = false;
    // Cancel the timer that checks for user activity
    if (this.activityPollSub !== undefined) {
      this.activityPollSub.unsubscribe();
    }
    // Cancel the timer for the session expiration warning
    if (this.popupSub !== undefined) {
      this.popupSub.unsubscribe();
    }
    // Cancel the timer for session expiration
    if (this.timeoutSub !== undefined) {
      this.timeoutSub.unsubscribe();
    }
  }
}
